import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, BehaviorSubject, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { ApiResponse } from "../models/api-response.model";

export interface AuthResponse {
  token: string | null; // null se requires2FA = true
  type: string; // e.g., "Bearer"
  username: string;
  roles: string[]; // e.g., ["ROLE_ADMIN"]
  requires2FA: boolean; // true se precisar código 2FA
}

export interface User {
  id?: number;
  name?: string;
  email?: string;
  username: string;
  cpf?: string;
  phone?: string;
  role: "candidate" | "admin" | "evaluator" | "supervisor";
  roles: string[] | string; // API roles format
}

export interface LoginCredentials {
  email: string;
  password: string;
  code2FA?: string; // Opcional - obrigatório apenas para admin na segunda tentativa
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  cpf: string;
  phone?: string;
}

export interface CandidateRegisterRequest {
  cpf: string;
  nomeCompleto: string;
  sexo: 'MASCULINO' | 'FEMININO' | 'OUTRO' | 'NAO_INFORMAR';
  desejaNomeSocial: boolean;
  nomeSocial?: string;
  dataNascimento: string;
  rg: string;
  nomePai?: string;
  profissaoPai?: string;
  nomeMae: string;
  nacionalidade: string;
  estadoCivil: 'SOLTEIRO' | 'CASADO' | 'DIVORCIADO' | 'VIUVO' | 'UNIAO_ESTAVEL' | 'OUTRO' | 'NAO_INFORMAR';
  nivelEscolaridade: 'FUNDAMENTAL' | 'MEDIO' | 'TECNICO' | 'SUPERIOR' | 'POS' | 'MESTRADO' | 'DOUTORADO' | 'OUTRO' | 'NAO_INFORMAR';
  numeroDependentes: number;
  telefonePessoal: string;
  outroTelefone?: string;
  email: string;
  emailConfirm: string;
  emailAlternativo?: string;
  senha: string;
  senhaConfirm: string;
  termosAceitos: boolean;
  receberInfoOrganizadora: boolean;
  declaracaoCanaisAtualizados: boolean;
  cidadeId?: number;
  cep: string;
  logradouro: string;
  numero: string;
  semNumero: boolean;
  complemento?: string;
  bairro: string;
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private apiUrl = `${environment.apiUrl}/auth`;

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadUserFromStorage();
  }

  /**
   * Login with email and password (supports 2FA integrated)
   */
  login(credentials: LoginCredentials): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login`, credentials)
      .pipe(
        tap(response => {
          // Only set session if 2FA is not required and token is present
          if (response && response.token && !response.requires2FA) {
            this.setSession(response);
          }
        })
      );
  }

  /**
   * Register new user
   */
  register(data: RegisterData): Observable<ApiResponse<AuthResponse>> {
    return this.http
      .post<ApiResponse<AuthResponse>>(`${this.apiUrl}/register`, data)
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.setSession(response.data);
          }
        })
      );
  }

  /**
   * Register new candidate
   */
  registerCandidate(data: CandidateRegisterRequest): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/public/candidatos`, data);
  }

  /**
   * Logout current user
   */
  logout(): void {
    this.http.post(`${this.apiUrl}/logout`, {}).subscribe();
    this.clearSession();
  }

  /**
   * Request password reset - sends code to email
   */
  requestPasswordReset(email: string): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/public/password-reset/request`, { email });
  }

  /**
   * Verify reset code
   */
  verifyResetCode(email: string, code: string): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/public/password-reset/validate`, { email, code });
  }

  /**
   * Reset password with code
   */
  resetPassword(email: string, code: string, newPassword: string): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/public/password-reset/confirm`, {
      email,
      code,
      newPassword
    });
  }

  /**
   * Refresh authentication token
   */
  refreshToken(): Observable<ApiResponse<AuthResponse>> {
    const refreshToken = this.getRefreshToken();
    return this.http
      .post<ApiResponse<AuthResponse>>(`${this.apiUrl}/refresh`, {
        refreshToken,
      })
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.setSession(response.data);
          }
        })
      );
  }

  /**
   * Get current user
   */
  /**
   * Verifica se o usuário atual é admin
   */
  isAdmin(): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    // Verificar roles (pode ser array ou string)
    if (Array.isArray(user.roles)) {
      return user.roles.some(role => 
        role.toUpperCase().includes('ADMIN') || role.toUpperCase() === 'ROLE_ADMIN'
      );
    }
    
    // Verificar role simples
    return user.role === 'admin' || 
           (typeof user.roles === 'string' && user.roles.toUpperCase().includes('ADMIN'));
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Fetch user profile from API and update local storage
   */
  fetchUserProfile(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/me`).pipe(
      tap(response => {
        const currentUser = this.getCurrentUser();
        if (currentUser && response) {
          // Update user with profile data
          const updatedUser: User = {
            ...currentUser,
            id: response.id,
            name: response.name || response.nomeCompleto || response.username,
            email: response.email,
            cpf: response.cpf,
            phone: response.telefone || response.phone
          };

          // Update localStorage and subject
          localStorage.setItem('user', JSON.stringify(updatedUser));
          this.currentUserSubject.next(updatedUser);
        }
      })
    );
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Check if token is expired
    const expiresAt = localStorage.getItem("expiresAt");
    if (!expiresAt) return false;

    return new Date().getTime() < parseInt(expiresAt);
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem("token");
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem("refreshToken");
  }

  /**
   * Set authentication session
   */
  private setSession(authResult: AuthResponse): void {
    // Store token and token type (only if token is present)
    if (authResult.token) {
      localStorage.setItem("token", authResult.token);
      localStorage.setItem("tokenType", authResult.type);
    }

    // Convert API roles to user role
    const role = this.mapRolesToUserRole(authResult.roles);

    // Create user object from auth response
    const user: User = {
      username: authResult.username,
      role: role,
      roles: authResult.roles
    };

    // Store user data
    localStorage.setItem("user", JSON.stringify(user));

    // Set expiration (default to 24 hours if not provided)
    const expiresAt = new Date().getTime() + (24 * 60 * 60 * 1000);
    localStorage.setItem("expiresAt", expiresAt.toString());

    this.currentUserSubject.next(user);

    // Fetch full user profile to populate name, email, etc.
    this.fetchUserProfile().subscribe({
      error: (err) => console.error('Error fetching user profile:', err)
    });
  }

  /**
   * Map API roles to user role
   */
  private mapRolesToUserRole(roles: string[]): User['role'] {
    if (roles.includes('ROLE_ADMIN')) return 'admin';
    if (roles.includes('ROLE_AVALIADOR')) return 'evaluator';
    if (roles.includes('ROLE_FISCAL')) return 'supervisor';
    return 'candidate'; // Default to candidate
  }

  /**
   * Clear authentication session
   */
  private clearSession(): void {
    localStorage.removeItem("token");
    localStorage.removeItem("tokenType");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("expiresAt");
    localStorage.removeItem("user");

    this.currentUserSubject.next(null);
  }

  /**
   * Get redirect path based on user role
   */
  getRedirectPath(role: User["role"]): string {
    switch (role) {
      case "admin":
        return "/portal/admin";
      case "candidate":
        return "/portal/candidato";
      case "evaluator":
        return "/portal/avaliador";
      case "supervisor":
        return "/fiscal";
      default:
        return "/portal";
    }
  }

  /**
   * Load user from storage
   */
  private loadUserFromStorage(): void {
    const userStr = localStorage.getItem("user");
    if (userStr && this.isAuthenticated()) {
      try {
        const user = JSON.parse(userStr);
        this.currentUserSubject.next(user);
      } catch (e) {
        this.clearSession();
      }
    }
  }
}
